import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { ResponsiveImage } from '../../../components';

import css from './ProgressiveResponsiveImage.module.css';

/**
 * Progressive-loading wrapper around ResponsiveImage for the
 * ListingPage carousel. Solves the "small image flashes to large
 * image" swap users see when they land on a ListingPage with the
 * cached SearchPage entity (only `listing-card` / `listing-card-2x`
 * variants present) before showListing() resolves the carousel's
 * `scaled-medium` / `scaled-large` / `scaled-xlarge` variants.
 *
 * Render strategy (Vinted / Etsy / Depop pattern):
 *   1. If a smaller cached variant exists, render it FIRST behind the
 *      sharp layer at full bleed, blurred + slightly scaled so the
 *      blur edge doesn't show through.
 *   2. If the requested high-res variants are available, render them
 *      on top via ResponsiveImage with opacity 0 → 1 on img.onLoad.
 *   3. Once the sharp layer has finished decoding, the blur layer
 *      stays underneath but is fully covered by the sharp `contain`
 *      image, so it costs nothing visually.
 *
 * When no cached fallback exists (deep-link navigation, no Redux
 * cache hit), the blur layer is skipped and we just fade the sharp
 * layer in once it loads. Equivalent to the "skeleton" approach for
 * that cold-start case without the empty grey state.
 */

const FALLBACK_VARIANT_PRIORITY = ['listing-card-2x', 'listing-card', 'scaled-small'];

const ProgressiveResponsiveImage = props => {
  const { image, variants, alt, sizes, className } = props;
  const [isHighResLoaded, setIsHighResLoaded] = useState(false);
  // Reset the loaded state when the underlying image entity changes,
  // e.g. the user navigates between slides in the carousel or the
  // entity gets a fresh set of variants after showListing resolves.
  const imageIdRef = useRef(image?.id?.uuid);
  useEffect(() => {
    const nextId = image?.id?.uuid;
    if (imageIdRef.current !== nextId) {
      imageIdRef.current = nextId;
      setIsHighResLoaded(false);
    }
  }, [image?.id?.uuid]);

  const availableVariants = image?.attributes?.variants || {};
  const hasHighRes = variants.some(v => availableVariants[v]?.url);

  // Best available fallback from the smaller variants — the
  // SearchPage entity carries these even when showListing hasn't
  // returned yet, so the blurred preview can render on first paint.
  const fallbackVariantKey = FALLBACK_VARIANT_PRIORITY.find(
    v => availableVariants[v]?.url
  );
  const fallbackUrl = fallbackVariantKey
    ? availableVariants[fallbackVariantKey].url
    : null;

  return (
    <div className={classNames(css.root, className)}>
      {fallbackUrl ? (
        <img
          src={fallbackUrl}
          alt=""
          aria-hidden="true"
          className={classNames(css.blurLayer, {
            [css.blurLayerHidden]: isHighResLoaded,
          })}
          draggable={false}
        />
      ) : null}
      {hasHighRes ? (
        <ResponsiveImage
          rootClassName={classNames(css.sharpLayer, {
            [css.sharpLayerVisible]: isHighResLoaded,
          })}
          image={image}
          alt={alt}
          variants={variants}
          sizes={sizes}
          onLoad={() => setIsHighResLoaded(true)}
        />
      ) : null}
    </div>
  );
};

export default ProgressiveResponsiveImage;
